import React, {useEffect, useRef} from 'react';
import {RefreshControl, View} from 'react-native';
import Animated from 'react-native-reanimated';
import {useHeaderHeight} from '@react-navigation/elements';
import {Image} from '@rneui/themed';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react';

import HomepageCarousel from '../../components/HomepageCarousel';
import {
  BANNER_ASPECT_RATIO_MOBILE,
  BANNER_ASPECT_RATIO_WEB,
  BANNER_DESKTOP_MAX_HEIGHT,
  BANNER_DESKTOP_MAX_WIDTH,
} from '../../components/HomepageCarousel/constants';
import {MemberPriceCarousel} from '../../components/ProductCarousel';
import BenefitsList from '../../components/rewards/BenefitsList';
import ExclusiveOffers from '../../components/rewards/ExclusiveOffers';
import {JoinMoreRewardsCTA} from '../../components/rewards/JoinMoreRewards';
import JustForYou from '../../components/rewards/JustForYou';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Promotions from '../../components/rewards/Promotions';
import ShopLoyaltyEnabled from '../../components/rewards/ShopLoyaltyEnabled';
import ViewMyCard from '../../components/rewards/ViewMyCard';
import {BannerSkeleton} from '../../components/skeleton';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {FOOTER_PARENT_STYLE, SiteFooter, usePageBleed} from '../../components/WebFooter/SiteFooter';
import {useScrollCollapseHandler} from '../../hooks/useScrollCollapse';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useHomepage} from '../../queries/homepage/useHomepage';
import {
  CONTENT_KEY_LOGGED_IN,
  CONTENT_KEY_LOGGED_OUT,
  CONTENT_KEY_SHOP_REWARDS_NOT_AVAILABLE,
  LoyaltyInformationItem,
} from '../../stores/loyaltyInformationStore';
import {useStores} from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';
import {openPageLink} from '../../utils/navigation';
import {
  brandRewardsCopy,
  resolveMemberDisplayName,
  rewardsMarketingName,
} from '../../utils/rewards';
import {RewardsTermsAndConditionsPopper} from '../rewards/TermsAndConditions';

type RewardsListScreenProps = RouteNavigatorScreenProps<
  AppRoute.REWARDS | AppRoute.ACCOUNT_REWARDS
> & {
  // DIS-1175: when embedded under the opaque ACCOUNT_REWARDS header
  // (AccountRewardsScreen opt-out view) the header already offsets content, so
  // the transparent-header top inset (paddingTop: headerHeight) is redundant and
  // leaves a ~158px gap. Suppress it there; keep it on the transparent REWARDS tab.
  applyHeaderInset?: boolean;
};

const RewardsListScreen = ({navigation, applyHeaderInset = true}: RewardsListScreenProps) => {
  const rootStore = useStores();
  const {rewardsStore, userStore, shopSelectionStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const pageBleed = usePageBleed();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listRef = useRef(null) as any;
  const {scrollHandler} = useScrollCollapseHandler(listRef);
  const headerHeight = useHeaderHeight();

  const selectedStore = shopSelectionStore?.selectedStore;
  const {data: homepage, isLoading: isHomepageLoading} = useHomepage({
    storeNumber: selectedStore?.store_number,
    storeState: selectedStore?.address?.administrative_area?.toUpperCase(),
    page: 'Rewards',
  });

  const navigateFromCarousel = (pageLink: string) => {
    openPageLink(pageLink);
  };

  const [, setRefreshing] = React.useState(false);

  const refresh = () => {
    setRefreshing(true);
    storeRef.current.load();
  };

  const loggedIn = userStore.isLoggedIn;

  const contentKey = !shopSelectionStore.loyaltyEnabled
    ? CONTENT_KEY_SHOP_REWARDS_NOT_AVAILABLE
    : loggedIn
      ? CONTENT_KEY_LOGGED_IN
      : CONTENT_KEY_LOGGED_OUT;

  const storeRef = useRef(new LoyaltyInformationItem(rootStore, contentKey));

  useEffect(() => {
    storeRef.current = new LoyaltyInformationItem(rootStore, contentKey);
    storeRef.current.load();
  }, [contentKey, rootStore]);

  const joinLoyaltyData = storeRef.current.data;

  // DIS-1633: persisted `hasRewards` outlives the session, so it alone showed the
  // member view to logged-out visitors. Lite sign-up sets hasRewards with no MFL
  // session, so isLoggedIn alone would delete that cohort. `isLiteMember` is the
  // marker going forward; isStaffSignup/isLmgStaff are retained as fallbacks for
  // devices whose persisted blob predates isLiteMember.
  const hasTrustedMembership =
    shopSelectionStore.loyaltyEnabled &&
    rewardsStore.hasRewards &&
    (userStore.isLoggedIn ||
      rewardsStore.isLiteMember ||
      rewardsStore.isStaffSignup ||
      rewardsStore.isLmgStaff);

  return (
    <ContentView testID="rewards-screen" parentStyle={FOOTER_PARENT_STYLE}>
      <Animated.ScrollView
        style={pageBleed}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
        refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} />}
        showsVerticalScrollIndicator={isWebDesktopScreen}
        showsHorizontalScrollIndicator={isWebDesktopScreen}
        contentContainerStyle={{
          paddingTop: applyHeaderInset ? headerHeight : 0,
          paddingBottom: isWebDesktopScreen ? 0 : 52,
        }}
        ref={listRef}>
        <ThemeProvider theme={rewardsTheme}>
          {hasTrustedMembership ? (
            /**
             * This is shown for when hasRewards true
             * shows rewards promotions available
             */
            <View
              style={
                isWebDesktopScreen
                  ? {
                      gap: 40,
                      marginTop: 20,
                    }
                  : {
                      gap: 24,
                      marginTop: 20,
                    }
              }>
              <View
                style={{
                  flexDirection: isWebDesktopScreen ? 'row' : 'column',
                  gap: isWebDesktopScreen ? 40 : 24,
                  marginBottom: isWebDesktopScreen ? 0 : 20,
                }}>
                <View
                  style={{
                    flexDirection: isWebDesktopScreen ? 'row' : 'column',
                    alignItems: 'center',
                    gap: isWebDesktopScreen ? 20 : 0,
                    flexGrow: 1,
                  }}>
                  <RewardsProgramImage variant="onLight" />
                  <Text style={{textAlign: 'center'}} variant="heading0">
                    WELCOME BACK TO {rewardsMarketingName.toUpperCase()}!
                  </Text>
                </View>
                <View
                  style={{
                    flexBasis: isWebDesktopScreen ? 230 : 'auto',
                    alignSelf: 'center',
                    width: '100%',
                  }}>
                  <ViewMyCard
                    name={resolveMemberDisplayName(
                      {firstName: rewardsStore.firstName, lastName: rewardsStore.lastName},
                      {firstName: userStore.firstName, lastName: userStore.lastName},
                      'RewardsListScreen',
                    )}
                    cardNumber={rewardsStore.cardNumber}
                    inStoreCode={rewardsStore.shortcode}
                    walletUrl={rewardsStore.walletUrl}
                    mode={isWebDesktopScreen ? 'print' : 'screen'}
                    memberSince={rewardsStore.memberSince}
                    isStaff={rewardsStore.isStaffSignup}
                    isLmgStaff={rewardsStore.isLmgStaff}
                  />
                </View>
              </View>

              {isHomepageLoading ? (
                <View>
                  <BannerSkeleton
                    showDots
                    aspectRatio={
                      isWebDesktopScreen ? BANNER_ASPECT_RATIO_WEB : BANNER_ASPECT_RATIO_MOBILE
                    }
                    maxHeight={isWebDesktopScreen ? BANNER_DESKTOP_MAX_HEIGHT : undefined}
                  />
                </View>
              ) : homepage?.carousel?.slides?.length ? (
                <View>
                  <HomepageCarousel
                    slides={homepage.carousel.slides}
                    onSlidePress={navigateFromCarousel}
                    testID="rewards-featured-carousel"
                  />
                </View>
              ) : null}

              {!shopSelectionStore.ecommerceDisabled && (
                <MemberPriceCarousel
                  title={brandRewardsCopy.memberPublicPricingTitle.toUpperCase()}
                  member_price="public"
                  onSeeAllPress={() => {
                    navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                      external_sell_type: 'members-price',
                    });
                  }}
                />
              )}

              {!shopSelectionStore.ecommerceDisabled && (
                <JustForYou
                  onSeeAllPress={() => {
                    navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                      external_sell_type: 'just-for-you',
                    });
                  }}
                />
              )}

              <ExclusiveOffers
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.EXCLUSIVE_OFFER_LIST);
                }}
              />

              <Promotions
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.PROMOTIONS_CURRENT);
                }}
              />
            </View>
          ) : joinLoyaltyData ? (
            /**
             * This is shown for when hasRewards false
             * for logged in or logged out user
             * showing content to encourage user to join and includes a join now CTA
             */
            <View
              style={
                isWebDesktopScreen
                  ? {
                      gap: 40,
                      marginTop: 40,
                    }
                  : {
                      gap: 24,
                    }
              }>
              <View>
                <Image
                  source={{
                    uri: isWebDesktopScreen
                      ? joinLoyaltyData?.imageDesktop?.url
                      : joinLoyaltyData?.image?.url,
                  }}
                  containerStyle={{
                    width: '100%',
                    aspectRatio: isWebDesktopScreen
                      ? BANNER_ASPECT_RATIO_WEB
                      : BANNER_ASPECT_RATIO_MOBILE,
                    flex: 1,
                    flexGrow: 1,
                    borderRadius: 10,
                    ...(isWebDesktopScreen
                      ? {
                          maxHeight: BANNER_DESKTOP_MAX_HEIGHT,
                          maxWidth: BANNER_DESKTOP_MAX_WIDTH,
                          alignSelf: 'center',
                        }
                      : {}),
                  }}
                />
                <Text variant="heading1">{joinLoyaltyData.headline}</Text>
                <Text variant="body2">{joinLoyaltyData.headlineCopy}</Text>

                <ShopLoyaltyEnabled>
                  <View style={{marginVertical: 24}}>
                    <JoinMoreRewardsCTA navigation={navigation} />
                  </View>
                </ShopLoyaltyEnabled>
              </View>

              {!shopSelectionStore.ecommerceDisabled && (
                <ShopLoyaltyEnabled>
                  <MemberPriceCarousel
                    title={`UNLOCK ${brandRewardsCopy.memberPublicPricingTitle.toUpperCase()}`}
                    member_price="public"
                    onSeeAllPress={() => {
                      navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                        external_sell_type: 'members-price',
                      });
                    }}
                  />
                </ShopLoyaltyEnabled>
              )}

              <View>
                <BenefitsList {...joinLoyaltyData} />

                <View style={{flexDirection: 'row'}}>
                  <Text variant="body2">{rewardsMarketingName} </Text>
                  <RewardsTermsAndConditionsPopper />
                </View>
              </View>
            </View>
          ) : null}
        </ThemeProvider>

        {!!isWebDesktopScreen && <View style={{flexGrow: 1, height: 100}} />}
        <SiteFooter />
      </Animated.ScrollView>
    </ContentView>
  );
};

export default observer(RewardsListScreen);
